import { ThemeConfig } from "antd";
import { MapToken } from "antd/es/theme/interface";
import { ConsumerTokens, ICustomTokens } from "@type/tokens";

const getAntdCustomTokens = (tokens: ConsumerTokens): ICustomTokens => ({
    Select: {
        colorBgFillSubtle: "#EAECF0",
    },
    Chips: {
        colorBorder: "#D0D5DD",
        colorText: "#3D3D3D",
        colorTextFgDisabled: "#98A2B3",
        colorBorderPrimaryBold: tokens?.color?.core?.primaryBold,
        colorBgFillPrimarySubtler: tokens?.color?.core?.primarySubtler,
        colorBorderDisabled: "#0000001A",
        colorBgFillDisabled: "#0000001A",
    },
});

const getMapTokens = (consumerTokens: ConsumerTokens): Partial<MapToken> => ({
    fontSizeHeading1: 30,
    fontSizeHeading2: 24,
    fontSizeHeading3: 20,
    fontSizeHeading4: 18,
    fontSizeHeading5: 14,
    colorBgMask: "#344054B2",
    colorPrimaryBg: consumerTokens?.color?.bg?.surface?.surfacePrimary,
    // Derived from figma designs: lineHeight in px / fontSize in px
    lineHeight: 20 / 14,
    lineHeightHeading1: 40 / 30,
    lineHeightHeading2: 32 / 24,
    lineHeightHeading3: 28 / 20,
    lineHeightHeading4: 26 / 18,
    lineHeightLG: 24 / 16,
    lineHeightSM: 18 / 12,
});

const getDefaultComponentTokens = (
    tokens: ConsumerTokens
): ThemeConfig["components"] & ICustomTokens => {
    const { Chips = {}, Select = {} } = getAntdCustomTokens(tokens);

    return {
        /**
         * @deprecated
         */
        Chips: { ...Chips },
        /**
         * @deprecated
         */
        Spin: { dotSizeSM: 32, dotSizeLG: 56, dotSize: 48, contentHeight: 400 },
        /**
         * @deprecated
         */
        Tabs: {
            cardBg: "transparent",
            cardPadding: "8px 12px",
            cardPaddingLG: "10px 14px",
            colorBgContainer: "#F9F5FF",
            colorBorderSecondary: "transparent",
            colorBorder: "transparent",
        },
        /**
         * @deprecated
         */
        Input: {
            paddingBlock: 4,
            paddingBlockLG: 8,
            paddingBlockSM: 0,
            paddingInline: 8,
            paddingInlineLG: 12,
        },
        /**
         * @deprecated
         */
        Table: {
            borderColor: "#EAECF0",
            headerBg: "#F9FAFB",
            headerColor: "#475467",
            fontFamily: "Inter",
            cellFontSize: 14,
            stickyScrollBarBg: "#EAECF0",
        },
        /**
         * @deprecated
         */
        Progress: {
            defaultColor: tokens?.color?.core?.primary,
        },
        /**
         * @deprecated
         */
        Select: {
            optionHeight: 40,
            multipleItemHeightLG: 24,
            multipleItemBg: "#FFF",
            multipleItemBorderColor: "#D0D5DD",
            optionActiveBg: "#F9FAFB",
            optionSelectedBg: "#F9FAFB",
            optionPadding: "8px 16px 8px 8px",
            optionSelectedFontWeight: "normal",
            ...Select,
        },
        /**
         * @deprecated
         */
        InputNumber: {
            paddingInline: 12,
            paddingInlineLG: 12,
        },
        /**
         * @deprecated
         */
        Segmented: {
            itemSelectedBg: tokens.color.bg.fill.default,
            itemSelectedColor: tokens.color.text.default,
            trackBg: tokens.color.bg.surface.surfaceLayer2,
        },
    };
};

function getAntdTokens(
    tokens: ConsumerTokens
): Partial<NonNullable<ThemeConfig["token"]>> {
    return {
        colorPrimary: tokens.color?.core?.primary,
        ...getMapTokens(tokens),
        ...getDefaultComponentTokens(tokens),
    };
}

export function getAntdThemeConfig(tokens: ConsumerTokens): ThemeConfig {
    return {
        token: getAntdTokens(tokens),
    };
}
